// placePin / placeMarker — atomic write of one hide.
//
// Day 13 schema: each "hide" creates a doc under
//   /markers/{uid_rid}/pins/{pinId}
// and updates the parent /markers/{uid_rid} metadata (placedBy, placedAt).
// Both writes go through writeBatch so we never end up with an orphan pin
// without parent metadata or vice versa (Codex P1#6: prior plan was
// addDoc + setDoc as separate calls, non-atomic).
//
// The parent doc's leaf fields (lat / lng / clue) from the Day 12 schema
// are NOT touched by new writes — they're preserved so a Day 12-era client
// rollback would still serve a coherent (legacy) marker. Migration script
// also writes through this path with deterministic pinIds.
//
// `markerDocId(userId, regionId)` stays deterministic; pinId is auto-generated
// by Firestore (or supplied by migration as `migrated_legacy`).

import { markerDocId, pinDoc, pinsCol } from "@goodbye/firebase";
import { getFirebase } from "@goodbye/firebase";
import {
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

export interface PlacePinInput {
  userId: string;
  regionId: string;
  lat: number;
  lng: number;
  clue: string;
  /** Worker email — used as placedBy audit trail */
  placedBy: string;
  /** Optional explicit pinId (migration uses this); defaults to auto-id */
  pinId?: string;
  /** Optional client-clock millisecond timestamp shared across a single
   *  「全員」/ multi-learner handleConfirm loop. Lets the worker later
   *  delete the whole batch at once when they realise they put pins in
   *  the wrong spot. Backwards-compatible: old pins without this field
   *  fall back to single-pin delete. */
  placedBatchAt?: number;
  /** Photo-hunt reward pin (瑞芳 only). When true, written as `reward: true`
   *  on the PinDoc; the learner Found page gates the personal-photo reveal
   *  on all reward pins being found. Omitted/false → ordinary pin, no field
   *  written (docs stay clean). */
  reward?: boolean;
  /** 「藏其他碎片」collectible pin. When true, written as `collectible: true`.
   *  Mutually exclusive with reward (caller passes only one). Payload content
   *  (payload*) is written separately via updateDoc after upload/transcode. */
  collectible?: boolean;
  /** 2026-05-30 公告 pin. When true, written as `announcement: true` — pure
   *  info, learner sees content but no action button. Mutually exclusive with
   *  reward/collectible. Payload (text/photo) written via updateDoc after. */
  announcement?: boolean;
}

export interface PlacePinResult {
  pinId: string;
}

/**
 * Atomically: create the pin under /markers/{uid_rid}/pins/{pinId}
 * and upsert parent metadata. Returns the auto-generated pinId.
 */
export async function placePin(input: PlacePinInput): Promise<PlacePinResult> {
  const { userId, regionId, lat, lng, clue, placedBy, pinId, placedBatchAt, reward, collectible, announcement } = input;
  const db = getFirebase().db;

  // Get a doc ref under the typed collection so converter applies.
  const pinsColRef = pinsCol(userId, regionId);
  // doc(collection, id?) returns a new ref with auto-id when id is omitted.
  // Cast pinsColRef to the untyped collection so TS lets us call doc() with
  // no id; the resulting ref uses the converter from the parent.
  const newPinRef = pinId
    ? doc(pinsColRef, pinId)
    : doc(pinsColRef);
  const parentMarkerRef = doc(db, "markers", markerDocId(userId, regionId));

  const batch = writeBatch(db);
  batch.set(newPinRef, {
    userId,
    regionId,
    lat,
    lng,
    clue: clue.trim() || "（無提示）",
    placedBy,
    placedAt: serverTimestamp(),
    // placedBatchAt:同次 handleConfirm 多人藏點共用此 client ms timestamp、
    // 後續可一次刪除整批。缺欄位 = 單人藏點、不可批刪。
    ...(placedBatchAt ? { placedBatchAt } : {}),
    // reward:瑞芳 photo-hunt 釘。只在 checked 時寫 reward:true、一般釘
    // 不帶此欄位、保持 doc 乾淨。
    ...(reward ? { reward: true } : {}),
    // collectible:「藏其他碎片」釘。payload* 內容由 caller 上傳/轉檔後
    // 用 updateDoc 補寫(放點時拿不到已上傳的 URL)。
    ...(collectible ? { collectible: true } : {}),
    // announcement:公告釘。純資訊、學員端無動作按鈕。payload* 同 collectible
    // 由 caller 補寫。
    ...(announcement ? { announcement: true } : {}),
    // foundAt deliberately omitted on create
  } as never);
  // Parent metadata: track latest hide. merge:true preserves legacy leaf
  // fields (lat/lng/clue from Day 12 — rollback safety) and existing
  // foundAt (which Day 12 learners may have written).
  batch.set(
    parentMarkerRef,
    {
      userId,
      regionId,
      placedBy,
      placedAt: serverTimestamp(),
    },
    { merge: true },
  );

  await batch.commit();
  return { pinId: newPinRef.id };
}

/**
 * Move an existing pin to a new lat/lng. Other fields (clue, photoUrl,
 * foundAt) preserved. Used when a worker realises the location's wrong
 * after a learner started looking — typically a few-meter correction.
 */
export async function movePin(input: {
  userId: string;
  regionId: string;
  pinId: string;
  lat: number;
  lng: number;
  /** Worker email — appended for audit trail */
  movedBy: string;
}): Promise<void> {
  await updateDoc(pinDoc(input.userId, input.regionId, input.pinId), {
    lat: input.lat,
    lng: input.lng,
    movedAt: serverTimestamp(),
    movedBy: input.movedBy,
  });
}

/**
 * Hard-delete a pin doc. Used when a worker placed in the wrong spot
 * and wants the slot back. Idempotent: deleting a non-existent doc is
 * a no-op in Firestore.
 *
 * Note: the parent marker doc is intentionally NOT touched. Legacy
 * Day-12 leaf fields on the parent are independent of the subcollection
 * and a future re-hide of the same (userId, regionId) will rewrite
 * placedAt/placedBy anyway.
 */
export async function removePin(input: {
  userId: string;
  regionId: string;
  pinId: string;
}): Promise<void> {
  await deleteDoc(pinDoc(input.userId, input.regionId, input.pinId));
}

/**
 * Batch-delete a set of pins identified by (userId, regionId, pinId) tuples.
 * Used by「· 移 除 同 批」when a worker did 「全員」/ multi-learner hide
 * and wants to undo the whole batch (e.g. wrong spot picked). All deletes
 * go through writeBatch so they're atomic (either all succeed or none).
 * Firestore allows up to 500 ops per batch — caller is responsible for
 * chunking if needed; 14 learners is well within the limit.
 */
export async function removePinBatch(
  pins: { userId: string; regionId: string; pinId: string }[],
): Promise<void> {
  if (pins.length === 0) return;
  const db = getFirebase().db;
  const batch = writeBatch(db);
  for (const p of pins) {
    batch.delete(pinDoc(p.userId, p.regionId, p.pinId));
  }
  await batch.commit();
}

/**
 * @deprecated use placePin. Kept temporarily so existing imports in
 * MapMark.tsx continue to compile during the PR-B1 transition. Re-exports
 * placePin under the old name.
 */
export const placeMarker = placePin;

/** @deprecated rename to PlacePinInput */
export type PlaceMarkerInput = PlacePinInput;

// Unused import in this file but kept exported for downstream:
void collection;
